import { createContext, type ReactNode, useContext, useState } from 'react';

type HeaderVisibilityContextValue = {
  hideHeaderOnMobile: boolean;
  setHideHeaderOnMobile: (hide: boolean) => void;
};

const HeaderVisibilityContext = createContext<HeaderVisibilityContextValue | null>(null);

/**
 * Controls whether the global HeaderNav is hidden on mobile viewports.
 * Used by immersive experiences (HDP) that take over the full viewport on mobile,
 * matching the legacy ListingPage pattern where the site header is replaced
 * by the HDP's own floating header.
 */
export function HeaderVisibilityProvider({
  children,
  initialHideHeaderOnMobile = false,
}: {
  children: ReactNode;
  /**
   * SSR seed for direct HDP loads: renders the initial HTML with the header
   * already hidden so the client-side hide on HDP mount doesn't shift layout.
   * After mount the HDP mount/unmount effects own the state.
   */
  initialHideHeaderOnMobile?: boolean;
}) {
  const [hideHeaderOnMobile, setHideHeaderOnMobile] = useState(initialHideHeaderOnMobile);

  return (
    <HeaderVisibilityContext.Provider value={{ hideHeaderOnMobile, setHideHeaderOnMobile }}>
      {children}
    </HeaderVisibilityContext.Provider>
  );
}

const defaultValue: HeaderVisibilityContextValue = {
  hideHeaderOnMobile: false,
  setHideHeaderOnMobile: () => {},
};

export function useHeaderVisibility(): HeaderVisibilityContextValue {
  const ctx = useContext(HeaderVisibilityContext);
  return ctx ?? defaultValue;
}
